import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import type { FaqCategory, CreateFaqCategoryInput } from '../../types';

interface CategoryFormProps {
  open: boolean;
  category: FaqCategory | null; // null = create mode
  sectionName: string;
  onClose: () => void;
  onSave: (data: CreateFaqCategoryInput) => Promise<void>;
}

export function CategoryForm({ open, category, sectionName, onClose, onSave }: CategoryFormProps) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setName(category?.name ?? '');
      setError('');
    }
  }, [open, category]);

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    try {
      await onSave({ name: name.trim() });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{category ? 'Edit Category' : 'Add Category'}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <Box fontSize={13} color="text.secondary">Section: <strong>{sectionName}</strong></Box>
          <TextField
            label="Category name"
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            required
          />
          {error && (
            <Box color="error.main" fontSize={13}>{error}</Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
